// Client-side funnel tracker for the AI lesson wizard: the five "saw it / did it"
// events the server cannot observe (which step the teacher reached, which AI
// suggestions they overrode, how long they reviewed, where they left).
//
// Explicitly NOT a GA4 wrapper. The events go to POST /api/ai-lessons/events and
// nowhere else — one data source, one definition per event, and DKT teachers (whom
// GA4 excludes) included.
//
// Contract, in order of importance:
//  1. It never throws and never blocks the UI. A measurement failure must be invisible.
//  2. A null/undefined/'' param is OMITTED, never coerced to 0 or ''. An explicitly
//     passed 0 / false / [] is KEPT — review_completed's edit counters are "0 or more",
//     and a missing counter would mean something different from a zero one.
//  3. Event names and enum values are closed sets. An unknown name is dropped with a
//     dev-only console error; a value outside an enum drops that one param.
//  4. The transport is INJECTED (from main.js). With no transport installed the module
//     is a total no-op — which is what keeps it inert under the wizard's unit specs:
//     they never run main.js, so nothing is queued, no timer is armed, no network is
//     touched, and no existing spec has to learn that this module exists.
import { newWizardRunId, getWizardRunId, clearWizardRunId } from './wizardRunId';

const FLUSH_MS = 2000;
const MAX_BATCH = 10;

const ENV = (typeof process !== 'undefined' && process.env) ? process.env : {};
const APP_VERSION = ENV.VUE_APP_VERSION || '';
const IS_DEV = ENV.NODE_ENV !== 'production';

// Closed, lowercase enum value sets, keyed by param name.
const ENUMS = {
  entry: ['cta', 'class', 'class_banner', 'direct'],
  step: ['type', 'input', 'review', 'publish', 'finish'],
  atStep: ['type', 'input', 'review', 'publish'],
  lessonType: ['reading', 'speaking'],
  inputMethod: ['text', 'url'],
  reason: ['navigate', 'unload', 'restart']
};

// The closed override set. `data.edited` is markSet's flag bag and carries MORE than
// overrides: ConversationExtract stamps partnerResetReason/partnerResetFrom into it,
// applyClassContext writes edited.tgtLang = false, and goal/phrases/studentInstruction/
// length are flagged too. Only these eight are "the teacher overrode the AI".
const OVERRIDE_FIELDS = [
  'level', 'ageGroup', 'title', 'srcLang', 'aiRole', 'userRole', 'topic', 'character'
];

// event name -> { param: kind }. This IS the contract: a param not listed here is
// dropped, so a typo at a call site is loud in dev and silent in production.
const SCHEMA = {
  studio_opened: {
    entry: 'enum', classId: 'str', locale: 'str', isRestart: 'bool'
  },
  step_reached: {
    step: 'enum', lessonType: 'enum', inputMethod: 'enum', secondsSinceOpen: 'int'
  },
  generation_started: {
    inputMethod: 'enum',
    overriddenFields: 'overrides',
    overrideCount: 'int',
    extractDegraded: 'bool',
    sourceChars: 'int',
    phrasesCount: 'int',
    isRegeneration: 'bool',
    lessonType: 'enum',
    jobId: 'str',
    lessonId: 'str'
  },
  review_completed: {
    reviewSeconds: 'int',
    tasksEdited: 'int',
    tasksDeleted: 'int',
    tasksRegenerated: 'int',
    passageEdited: 'bool',
    titleEdited: 'bool',
    taskCountFinal: 'int',
    previewOpened: 'bool',
    lessonType: 'enum',
    lessonId: 'str'
  },
  wizard_abandoned: {
    atStep: 'enum',
    reason: 'enum',
    hadGeneration: 'bool',
    generating: 'bool',
    secondsSinceOpen: 'int',
    lessonType: 'enum',
    lessonId: 'str'
  }
};

const EVENT_NAMES = Object.keys(SCHEMA);

let queue = [];
let timer = null;
let transport = null;

function warn(message, detail) {
  if (!IS_DEV) return;
  // eslint-disable-next-line no-console
  console.error('[ai-wizard-tracker]', message, detail === undefined ? '' : detail);
}

// Install the network sender. Called once, from main.js.
export function setTransport(fn) {
  transport = (typeof fn === 'function') ? fn : null;
}

// Returns undefined for "omit me". An explicitly passed 0 / false / [] survives.
function coerce(name, key, kind, value) {
  if (value === null || value === undefined) return undefined;
  if (kind === 'bool') return !!value;
  if (kind === 'int') {
    const n = Number(value);
    return isFinite(n) ? Math.round(n) : undefined;
  }
  if (kind === 'str') {
    const s = String(value);
    return s === '' ? undefined : s;
  }
  if (kind === 'enum') {
    const s = String(value).toLowerCase();
    if ((ENUMS[key] || []).indexOf(s) === -1) {
      warn(`${name}: "${value}" is not in the closed set for "${key}"`);
      return undefined;
    }
    return s;
  }
  if (kind === 'overrides') {
    if (!Array.isArray(value)) return undefined;
    // Second line of defence against data.edited's non-override keys (the screen
    // filters too, and so does the backend).
    return value.map(String).filter(f => OVERRIDE_FIELDS.indexOf(f) !== -1);
  }
  return undefined;
}

// Flat shape on purpose: the backend's logger validates each key against the event
// model's own schema paths, so a nested `params` bag would just have to be unwrapped.
function buildEvent(name, params) {
  const schema = SCHEMA[name];
  const raw = params || {};
  const out = { event: name, ts: new Date().toISOString(), wizardRunId: getWizardRunId() };
  if (APP_VERSION) out.appVersion = APP_VERSION;
  Object.keys(schema).forEach((key) => {
    const v = coerce(name, key, schema[key], raw[key]);
    if (v !== undefined) out[key] = v;
  });
  if (IS_DEV) {
    Object.keys(raw).forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(schema, key)) warn(`${name}: unknown param "${key}"`);
    });
  }
  return out;
}

// `keepalive` is for the beforeunload path only: the document is going away, so the
// batch has to leave in this very tick AND survive the unload.
export function flush(keepalive) {
  try {
    if (timer) { clearTimeout(timer); timer = null; }
    if (!queue.length || !transport) return;
    const batch = queue;
    queue = [];
    transport(batch, !!keepalive);
  } catch (e) {
    warn('flush failed', e && e.message);
  }
}

function arm() {
  if (timer) return;
  timer = setTimeout(() => { timer = null; flush(false); }, FLUSH_MS);
}

export function track(name, params) {
  try {
    if (EVENT_NAMES.indexOf(name) === -1) {
      warn(`unknown event name "${name}"`);
      return;
    }
    if (!transport) return; // no transport installed -> total no-op (see the header)
    queue.push(buildEvent(name, params));
    if (queue.length >= MAX_BATCH) { flush(false); return; }
    arm();
  } catch (e) {
    warn('track failed', e && e.message);
  }
}

export function trackAndFlushNow(name, params) {
  track(name, params);
  flush(true);
}

// One wizard run = one correlation id. Rotated BEFORE the run's first event, so that
// event and every /api/ai-lessons/ request of the run share it.
export function startRun() {
  flush(false); // whatever is still queued belongs to the run that just ended
  return newWizardRunId();
}

export function endRun() {
  flush(false);
  clearWizardRunId();
}

// data.edited -> the closed override set, in a stable order.
export function overriddenFieldsFrom(edited) {
  const e = edited || {};
  return OVERRIDE_FIELDS.filter(k => !!e[k]);
}



// WEBPACK FOOTER //
// ./src/components/modules/aiLesson/wizardTracker.js